import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { Scheduler, WeekView, DayView, Appointments, Toolbar, DateNavigator, TodayButton } from '@devexpress/dx-react-scheduler-material-ui'
import { ViewState } from '@devexpress/dx-react-scheduler'
import { AvailableShift } from '@/core/models/planning.model'
import dayjs from 'dayjs'
import { toAdapterLocale } from '@/utils/locale'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    schedulerContainer: {
        flexGrow: 1,
        overflow: 'auto',
    },
    toolbar: {
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f9f9f9',
    },
    appointment: {
        borderRadius: '4px',
        '&:hover': {
            opacity: 0.9,
        },
    },
    availableAppointment: {
        backgroundColor: theme.palette.success.light,
        '&:hover': {
            backgroundColor: theme.palette.success.main,
        },
    },
    limitedAppointment: {
        backgroundColor: theme.palette.warning.light,
        '&:hover': {
            backgroundColor: theme.palette.warning.main,
        },
    },
}))

interface ShiftCalendarViewProps {
    shifts: AvailableShift[]
    isLoading: boolean
    error: any
    onClaimShift: (shift: AvailableShift) => void
    onWeekChange?: (startDate: Date, endDate: Date) => void
}

// Custom appointment component
const Appointment = ({ children, style, data, ...restProps }: any) => {
    const classes = useStyles()
    const isLimited = data.availableSpots <= 2
    const { onAppointmentClick } = data

    return (
        <Appointments.Appointment
            {...restProps}
            style={{
                ...style,
                backgroundColor: isLimited ? '#ff9800' : '#4caf50',
                borderRadius: '4px',
                cursor: 'pointer'
            }}
            data={data}
            className={`${classes.appointment} ${isLimited ? classes.limitedAppointment : classes.availableAppointment}`}
            onClick={() => onAppointmentClick && onAppointmentClick(data)}
        >
            {children}
        </Appointments.Appointment>
    )
}

// Custom appointment content
const AppointmentContent = ({ children, data, ...restProps }: any) => {
    const { t } = useTranslation()

    return (
        <Appointments.AppointmentContent {...restProps} data={data}>
            <Box sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" noWrap>
                    {data.title}
                </Typography>
                <Typography variant="caption" display="block">
                    {t('page.shiftMarketplace.availableSpots', 'Available')}: {data.availableSpots}/{data.totalSpots}
                </Typography>
                {data.planningName && (
                    <Typography variant="caption" display="block" noWrap>
                        {data.planningName}
                    </Typography>
                )}
            </Box>
        </Appointments.AppointmentContent>
    )
}

export function ShiftCalendarView({ shifts, isLoading, error, onClaimShift, onWeekChange }: ShiftCalendarViewProps) {
    const { t, i18n } = useTranslation()
    const classes = useStyles()
    const theme = useTheme()
    // A 7-day week grid is unusable at ~390px, so fall back to a single-day view on phones.
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [currentDate, setCurrentDate] = useState(new Date())
    const initialChangeRef = useRef(false)

    // Calculate and notify parent of week range when current date changes
    useEffect(() => {
        // Skip the initial effect call if data is already loaded
        // This prevents the infinite loop when the component first mounts
        if (onWeekChange && (initialChangeRef.current || shifts.length === 0)) {
            const startOfWeek = dayjs(currentDate).startOf('week').toDate()
            const endOfWeek = dayjs(currentDate).endOf('week').toDate()
            onWeekChange(startOfWeek, endOfWeek)
        }
        initialChangeRef.current = true
    }, [currentDate, onWeekChange])

    const handleDateChange = (newDate: Date) => {
        setCurrentDate(newDate)
    }

    // Transform shifts into scheduler data
    const schedulerData = shifts.map(shift => ({
        id: `${shift.planningId}-${shift.shiftTemplateId}-${shift.startTime}`,
        startDate: new Date(shift.startTime),
        endDate: new Date(shift.endTime),
        title: shift.location,
        availableSpots: shift.availableSpots,
        totalSpots: shift.totalSpots,
        planningName: shift.planningName,
        consultationReason: shift.consultationReason,
        // Store the original shift data for the click handler
        originalShift: shift,
        // Add the click handler to the data
        onAppointmentClick: (data: any) => {
            if (data.originalShift) {
                onClaimShift(data.originalShift)
            }
        }
    }))

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('page.shiftMarketplace.errorLoadingData', 'Error loading data. Please try again.')}
            </Alert>
        )
    }

    return (
        <Box className={classes.root} data-testid="shift-marketplace-calendar" data-view={isMobile ? 'day' : 'week'}>
            <Scheduler
                data={schedulerData}
                height="auto"
                locale={toAdapterLocale(i18n.language)}
            >
                <ViewState
                    currentDate={currentDate}
                    onCurrentDateChange={handleDateChange}
                />
                <Toolbar />
                <DateNavigator />
                <TodayButton />
                {isMobile ? (
                    <DayView startDayHour={7} endDayHour={20} cellDuration={60} />
                ) : (
                    <WeekView startDayHour={7} endDayHour={20} cellDuration={60} />
                )}
                <Appointments
                    appointmentComponent={Appointment}
                    appointmentContentComponent={AppointmentContent}
                />
            </Scheduler>
        </Box>
    )
}

export default ShiftCalendarView
